<!-- PharmacyList.vue -->
<template>
  <div class="pharmacy-app">
    <!-- Header -->
    <div class="header">
      <h1>List of Available Pharmacies</h1>
      <!-- Menu Bar - Updated with Avatar and Cart -->
      <div class="menu-bar">
        <!-- Cart Icon with Badge -->
        <div class="menu-item cart-menu" @click="toggleDropdown('cart')">
          <div class="cart-icon-container">
            <svg class="cart-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <circle cx="9" cy="21" r="1"></circle>
              <circle cx="20" cy="21" r="1"></circle>
              <path d="m1 1 4 4 l0 13a5,5 0 0 0 5,5h9a5,5 0 0 0 5-5V9H8"></path>
            </svg>
            <span v-if="totalCartItems > 0" class="cart-badge">{{ totalCartItems }}</span>
          </div>
          <!-- Cart Dropdown -->
          <div class="dropdown-menu cart-dropdown" v-show="activeDropdown === 'cart'">
            <div v-if="cartItems.length === 0" class="cart-empty">
              <p>Your cart is empty</p>
            </div>
            <div v-else>
              <div class="cart-header">
                <h4>From {{ selectedPharmacyName }}</h4>
              </div>
              <div class="cart-header">
                <h4>Cart ({{ totalCartItems }} Qtys)</h4>
                <span class="cart-total">৳{{ cartTotal.toFixed(2) }}</span>
              </div>
              
              <div class="cart-items-list">
                <div v-for="item in cartItems" :key="item.medicine.id" class="cart-item">
                  <div class="cart-item-info">
                    <span class="medicine-name">{{ item.medicine.name }}</span>
                    <div class="cart-item-details">
                      <span class="quantity">Qty: {{ item.quantity }}</span>
                      <span class="price">৳{{ (parseFloat(item.medicine.tp_amount || 0) * item.quantity).toFixed(2) }}</span>
                    </div>
                  </div>
                  <button class="remove-item" @click="removeFromCart(item.medicine)" title="Remove from cart">
                    ✕
                  </button>
                </div>
              </div>
              <div class="cart-actions">
                <button class="clear-cart-btn" @click="clearCart">Clear Cart</button>
                <button class="checkout-btn" @click="proceedToCheckout">Checkout</button>
              </div>
            </div>
          </div>
        </div>

        <!-- User Avatar Menu -->
        <div class="menu-item" @click="toggleDropdown('menu')">
          <div class="user-avatar">
            <img src="/image/user_avater.png" alt="User Avatar" class="avatar-img" />
          </div>
          <div class="dropdown-menu menu-dropdown" v-show="activeDropdown === 'menu'">
            <div class="dropdown-item" @click.stop="navigateToMyOrders">
              <i class="icon-orders"></i>
              My Orders
            </div>
            <div class="dropdown-item" @click.stop="logout">
              <i class="icon-logout"></i>
              Logout
            </div>
          </div>
        </div>
      </div>
      <div v-if="locationStatus" class="location-status" :class="locationStatus.type">
        {{ locationStatus.message }}
      </div>
    </div>

    <!-- Toast Message -->
    <div v-if="showToast" class="toast-message" :class="toastType">
      {{ toastMessage }}
    </div>

    <!-- Filter and search controls -->
    <div class="control-panel">
      <div class="filter-city">
        <button class="location-button" :class="{ 'active-filter': isLocationActive }" @click="requestLocation" :disabled="isLocationLoading">
          <span v-if="isLocationLoading">Getting...</span>
          <span v-else>{{ userLocation ? 'Nearby Pharmacies' : 'Use my location' }}</span>
        </button>
        <div class="filter-item" :class="{ 'active-filter': isAreaFilterActive }" @click="toggleDropdown('city')">
            <span>{{ selectedLocationDisplay }}</span>
            <i class="down-arrow" :class="{ 'rotated': activeDropdown === 'city' }"></i>

          <!-- Updated dropdown with cascading functionality -->
          <div class="dropdown-search-menu cascading-menu" v-show="activeDropdown === 'city'">
            <!-- All City option -->
            <div class="dropdown-item" @click.stop="selectAllCity()">
              Choose Area
            </div>
            
            <!-- Districts with hover functionality -->
            <div 
              v-for="district in availableDistricts" 
              :key="district"
              class="dropdown-item district-item"
              @mouseenter="loadThanas(district)"
              @click.stop="selectDistrict(district)"
            >
              {{ district }}
              <span class="arrow-right">›</span>
              
              <!-- Thana submenu -->
              <div 
                v-if="hoveredDistrict === district && thanasForDistrict.length > 0"
                class="thana-submenu"
                @mouseenter="keepSubmenuOpen"
                @mouseleave="hideSubmenu"
              >
                <div 
                  v-for="thana in thanasForDistrict" 
                  :key="thana"
                  class="dropdown-item thana-item"
                  @click.stop="selectDistrictAndThana(district, thana)"
                >
                  {{ thana }}
                </div>
              </div>
            </div>
            
            <!-- Loading indicator for thanas -->
            <div v-if="isLoadingThanas" class="dropdown-item loading-item">
              Loading areas...
            </div>
          </div>
        </div>
        
      </div>
      <div class="search-bar">
        <input type="text" placeholder="Search pharmacies..." v-model="searchQuery" />
      </div>
    </div>
   
    <!-- Loading indicator for API -->
    <div v-if="isLoading" class="loading-indicator">
      <div class="loading-spinner"></div>
      <p>{{ loadingMessage }}</p>
    </div>

    <!-- Error message for API -->
    <div v-else-if="apiError" class="no-results">
      <p>{{ apiError }}</p>
      <button class="order-button" @click="retryLastAction">Retry</button>
    </div>

    <!-- Pharmacy listings -->
    <div v-else class="pharmacy-list">
      <div v-if="isLocationLoading" class="loading-indicator">
        <div class="loading-spinner"></div>
        <p>Finding nearby pharmacies...</p>
      </div>
      <div v-else-if="filteredPharmacies.length === 0" class="no-results">
        <p>{{ getNoResultsMessage() }}</p>
      </div>
      <div v-for="pharmacy in filteredPharmacies" :key="pharmacy.id" class="pharmacy-item" :class="{ 'in-cart': isSelectedPharmacy(pharmacy.id) }">
        <div class="pharmacy-img">
          <img :src="pharmacy.logo || '/assets/pharmacy-placeholder.png'" alt="Pharmacy image" class="placeholder-img"/>
        </div>
        
        <div class="pharmacy-info">
          <div class="pharmacy-header">
            <h3>{{ pharmacy.name }}</h3>
            <div class="pharmacy-badges">
              <span v-if="pharmacy.distance" class="badge distance">{{ pharmacy.distance.toFixed(1) }}km</span>
            </div>
          </div>
          <div class="address">{{ pharmacy.address }}</div>
          <button class="order-button" @click="orderNow(pharmacy)">Order Now</button>
        </div>
      </div>
    </div>
    
    <!-- Footer -->
    <div class="footer">
      <p>© {{ new Date().getFullYear() }} Pharmacy App</p>
    </div>
  </div>
</template>
  
<script>
import PharmacyApiService from '@/services/PharmacyApiService';

export default {
  name: 'PharmacyList',
  data() {
    return {
      selectedCity: 'Choose Area',
      selectedDistrict: null,
      selectedThana: null,
      selectedPharmacyName: '',
      pharmacies: [],
      searchQuery: '',
      activeDropdown: null,
      userLocation: null,
      isLocationLoading: false,
      locationStatus: null,
      maxDistance: 250, // Default 250km radius
      isLoading: true,
      apiError: null,
      loadingMessage: 'Loading pharmacy data...',
      currentUser: {
        userId: '',
        userName: '',
        userMobile: '',
        userAddress: '',
        userEmail: ''
      },
      // Cart related data
      cartItems: [],
      showToast: false,
      toastMessage: '',
      toastType: 'info',
      
      // New data for cascading dropdown
      availableDistricts: [],
      thanasForDistrict: [],
      hoveredDistrict: null,
      isLoadingThanas: false,
      isLoadingDistricts: false,
      lastAction: 'fetchPharmacies' // Track last action for retry functionality
    }
  },
  computed: {
    isLocationActive() {
    // Glow when showing nearby pharmacies (either user location is available OR location is being loaded)
    return (this.userLocation || this.isLocationLoading) && (this.selectedCity === 'Choose Area' && !this.selectedDistrict);
    },
    
    isAreaFilterActive() {
      return this.selectedDistrict || (this.selectedCity !== 'Choose Area' && !this.userLocation);
    },
    selectedLocationDisplay() {
      if (this.selectedDistrict && this.selectedThana) {
        return `${this.selectedDistrict} - ${this.selectedThana}`;
      } else if (this.selectedDistrict) {
        return this.selectedDistrict;
      } else {
        return this.selectedCity;
      }
    },
    
    filteredPharmacies() {
      let result = this.pharmacies.slice();
      
      // Apply search query filter
      if (this.searchQuery) {
        result = result.filter(pharmacy => 
          pharmacy.name.toLowerCase().includes(this.searchQuery.toLowerCase()) || 
          pharmacy.address.toLowerCase().includes(this.searchQuery.toLowerCase())
        );
      }
      
      // Apply city filter (legacy support)
      if (this.selectedCity !== 'Choose Area' && !this.selectedDistrict) {
        result = result.filter(pharmacy => 
          pharmacy.address.includes(this.selectedCity)
        );
      }
      
      // Apply location-based filter if user location is available
      if (this.userLocation && this.selectedCity === 'Choose Area' && !this.selectedDistrict) {
        result = result.filter(pharmacy => {
          return pharmacy.distance !== undefined && pharmacy.distance <= this.maxDistance;
        });
        
        // Sort by distance when location is available
        result.sort((a, b) => a.distance - b.distance);
      }
      
      return result;
    },
    
    totalCartItems() {
      return this.cartItems.reduce((total, item) => total + item.quantity, 0);
    },
    
    cartTotal() {
      return this.cartItems.reduce((total, item) => 
        total + (parseFloat(item.medicine.tp_amount || 0) * item.quantity), 0);
    }
  },
  methods: {
    // Load user data from localStorage
    loadUserData() {
      this.currentUser = {
        userId: localStorage.getItem('userId') || '',
        userName: localStorage.getItem('userName') || '',
        userMobile: localStorage.getItem('userMobile') || '',
        userAddress: localStorage.getItem('userAddress') || '',
        userEmail: localStorage.getItem('userEmail') || ''
      }
      
      console.log('Current user data loaded:', this.currentUser)
    },
    
    // Check if user is logged in
    checkAuthStatus() {
      const isLoggedIn = localStorage.getItem('isLoggedIn')
      if (isLoggedIn !== 'true') {
        console.log('User not logged in, redirecting to login')
        this.$router.push('/login')
        return false
      }
      return true
    },
    
    // Load cart items from sessionStorage
    loadCartItems() {
      const storedCartItems = sessionStorage.getItem('cartItems')
      if (storedCartItems) {
        try {
          this.cartItems = JSON.parse(storedCartItems)
          console.log('Restored cart items from session:', this.cartItems.length)
        } catch (e) {
          console.error('Error parsing stored cart items:', e)
        }
      }
    },

    // Cart methods
    removeFromCart(medicine) {
      this.cartItems = this.cartItems.filter(item => item.medicine.id !== medicine.id)
      this.updateCartInSession()
      console.log(`Removed ${medicine.name} from cart`)
    },

    clearCart() {
      if (this.cartItems.length === 0) {
        this.showToastMessage('Cart is already empty', 'info')
        return
      }
      
      if (confirm('Are you sure you want to clear your cart?')) {
        this.cartItems = []
        sessionStorage.removeItem('cartItems')
        sessionStorage.removeItem('selectedPharmacy')
        this.showToastMessage('Cart cleared successfully', 'success')
      }
    },

    // Cart functionality
    isSelectedPharmacy(pharmacyId) {
      const selectedPharmacy = sessionStorage.getItem('selectedPharmacy')
      if (!selectedPharmacy) return false
      
      try {
        const parsedPharmacy = JSON.parse(selectedPharmacy)
        return parsedPharmacy.id === pharmacyId
      } catch (e) {
        return false
      }
    },

    proceedToCheckout() {
      if (this.cartItems.length === 0) {
        this.showToastMessage('Please add items to your cart first', 'warning')
        return
      }
      
      // Navigate to checkout
      this.$router.push('/checkout')
    },

    updateCartInSession() {
      sessionStorage.setItem('cartItems', JSON.stringify(this.cartItems))
    },

    showToastMessage(message, type = 'info') {
      this.toastMessage = message
      this.toastType = type
      this.showToast = true
      
      setTimeout(() => {
        this.showToast = false
      }, 7000)
    },
    
    // Enhanced API methods
    async fetchDistricts() {
      if (this.isLoadingDistricts) return;
      
      this.isLoadingDistricts = true;
      try {
        console.log('Fetching districts...');
        this.availableDistricts = await PharmacyApiService.fetchDistricts();
        console.log('Loaded districts:', this.availableDistricts);
      } catch (error) {
        console.error('Error fetching districts:', error);
        this.showToastMessage('Failed to load districts', 'error');
      } finally {
        this.isLoadingDistricts = false;
      }
    },
    
    async loadThanas(district) {
      if (this.hoveredDistrict === district && this.thanasForDistrict.length > 0) {
        return; // Already loaded
      }
      
      this.hoveredDistrict = district;
      this.isLoadingThanas = true;
      this.thanasForDistrict = [];
      
      try {
        console.log('Loading thanas for district:', district);
        this.thanasForDistrict = await PharmacyApiService.fetchThanas(district);
        console.log('Loaded thanas:', this.thanasForDistrict);
      } catch (error) {
        console.error('Error loading thanas:', error);
        // this.showToastMessage('Failed to load areas for ' + district, 'error');
      } finally {
        this.isLoadingThanas = false;
      }
    },
    
    keepSubmenuOpen() {
      // Prevent submenu from closing when hovering over it
    },
    
    hideSubmenu() {
      setTimeout(() => {
        this.hoveredDistrict = null;
        this.thanasForDistrict = [];
      }, 200); // Small delay to allow clicking
    },
    
    // Selection methods
    selectAllCity() {
      this.selectedCity = 'Choose Area';
      this.selectedDistrict = null;
      this.selectedThana = null;
      this.activeDropdown = null;
      this.lastAction = 'fetchPharmacies';
      this.fetchPharmacies();
    },
    
    selectDistrict(district) {
      console.log('Selected district only:', district);
      this.selectedDistrict = district;
      this.selectedThana = null;
      this.selectedCity = district;
      this.activeDropdown = null;
      
      this.lastAction = 'fetchPharmaciesByDistrict';
      this.fetchPharmaciesByDistrict(district);
    },
   
    selectDistrictAndThana(district, thana) {
      console.log('Selected district and thana:', district, thana);
      this.selectedDistrict = district;
      this.selectedThana = thana;
      this.selectedCity = `${district} - ${thana}`;
      this.activeDropdown = null;
      
      this.lastAction = 'fetchPharmaciesByLocation';
      this.fetchPharmaciesByLocation(district, thana);
    },
    
    // API fetch methods
    fetchPharmacies() {
      this.isLoading = true;
      this.loadingMessage = 'Loading pharmacy data...';
      this.apiError = null;

      PharmacyApiService.fetchPharmacies()
        .then(pharmacies => {
          this.pharmacies = pharmacies;
          console.log('Loaded', this.pharmacies.length, 'pharmacies from API');
          
          // Calculate distances if user location is available
          if (this.userLocation) {
            this.updatePharmacyDistances();
          }
        })
        .catch(error => {
          console.error('Error fetching pharmacy data:', error);
          this.apiError = 'Failed to load pharmacy data. Please try again.';
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    async fetchPharmaciesByDistrict(district) {
      this.isLoading = true;
      this.loadingMessage = `Loading pharmacies in ${district}...`;
      this.apiError = null;
      
      try {
        // For district-only selection, we need to get all thanas first, then fetch all pharmacies
        const thanas = await PharmacyApiService.fetchThanas(district);
        let allPharmacies = [];
        
        for (const thana of thanas) {
          try {
            const pharmacies = await PharmacyApiService.fetchPharmaciesByLocation(district, thana);
            allPharmacies = allPharmacies.concat(pharmacies);
          } catch (error) {
            console.warn(`Failed to fetch pharmacies for ${district}-${thana}:`, error);
          }
        }
        
        this.pharmacies = allPharmacies;
        console.log('Loaded', this.pharmacies.length, 'pharmacies for district:', district);
        
        if (this.userLocation) {
          this.updatePharmacyDistances();
        }
      } catch (error) {
        console.error('Error fetching pharmacies by district:', error);
        this.apiError = `Failed to load pharmacies for ${district}. Please try again.`;
      } finally {
        this.isLoading = false;
      }
    },
    
    fetchPharmaciesByLocation(district, thana) {
      this.isLoading = true;
      this.loadingMessage = `Loading pharmacies in ${district} - ${thana}...`;
      this.apiError = null;
      
      PharmacyApiService.fetchPharmaciesByLocation(district, thana)
        .then(pharmacies => {
          this.pharmacies = pharmacies;
          console.log('Loaded', this.pharmacies.length, 'pharmacies for', district, '-', thana);
          
          if (this.userLocation) {
            this.updatePharmacyDistances();
          }
        })
        .catch(error => {
          console.error('Error fetching pharmacies by location:', error);
          this.apiError = `Failed to load pharmacies for ${district} - ${thana}. Please try again.`;
        })
        .finally(() => {
          this.isLoading = false;
        });
    },
    
    retryLastAction() {
      switch (this.lastAction) {
        case 'fetchPharmacies':
          this.fetchPharmacies();
          break;
        case 'fetchPharmaciesByDistrict':
          if (this.selectedDistrict) {
            this.fetchPharmaciesByDistrict(this.selectedDistrict);
          }
          break;
        case 'fetchPharmaciesByLocation':
          if (this.selectedDistrict && this.selectedThana) {
            this.fetchPharmaciesByLocation(this.selectedDistrict, this.selectedThana);
          }
          break;
        default:
          this.fetchPharmacies();
      }
    },
    
    getNoResultsMessage() {
      if (this.selectedDistrict && this.selectedThana) {
        return `No pharmacies found in ${this.selectedDistrict} - ${this.selectedThana}. Try selecting a different area.`;
      } else if (this.selectedDistrict) {
        return `No pharmacies found in ${this.selectedDistrict}. Try selecting a different district.`;
      } else if (this.searchQuery) {
        return 'No pharmacies found matching your search. Try different keywords.';
      } else {
        return 'No pharmacies found. Try adjusting your search or location settings.';
      }
    },
    
    orderNow(pharmacy) {
      // Check if cart has items from a different pharmacy
      const selectedPharmacy = sessionStorage.getItem('selectedPharmacy')
      if (selectedPharmacy && this.cartItems.length > 0) {
        const parsedPharmacy = JSON.parse(selectedPharmacy)
        if (parsedPharmacy.id !== pharmacy.id) {
          this.showToastMessage(`Your selected items from ${parsedPharmacy.name} are still in the cart. Please proceed to checkout or clear the cart before selecting another pharmacy.`, 'warning')
          return
        }
      }

      console.log('Order button clicked for pharmacy:', pharmacy.name, 'ID:', pharmacy.id);
      
      // Store current user data in sessionStorage for the checkout flow
      sessionStorage.setItem('currentUser', JSON.stringify(this.currentUser))
      
      // Store selected pharmacy
      sessionStorage.setItem('selectedPharmacy', JSON.stringify({
        id: pharmacy.id,
        name: pharmacy.name,
        address: pharmacy.address
      }))
      
      // Navigate to medicine list with pharmacy information
      this.$router.push({
        name: 'MedicineList',
        params: {
          pharmacyId: pharmacy.id,
          pharmacyName: pharmacy.name,
        },
        // Properly pass the pharmacy data in the state
        state: {
          pharmacy: {
            id: pharmacy.id,
            name: pharmacy.name,
            address: pharmacy.address
          },
          user: this.currentUser
        }
      });
    },
    
    toggleDropdown(dropdown) {
      if (this.activeDropdown === dropdown) {
        this.activeDropdown = null;
        this.hoveredDistrict = null;
        this.thanasForDistrict = [];
      } else {
        this.activeDropdown = dropdown;
        
        // Load districts when opening city dropdown
        if (dropdown === 'city' && this.availableDistricts.length === 0) {
          this.fetchDistricts();
        }
      }
    },
    
    selectCity(city) {
      // Legacy method - keeping for backward compatibility
      this.selectedCity = city;
      this.selectedDistrict = null;
      this.selectedThana = null;
      this.activeDropdown = null;
      console.log(`City selected: ${city}`);
    },
    
    // Navigation methods
    navigateToMyOrders() {
      this.activeDropdown = null;
      console.log('Navigating to My Orders page');
      this.$router.push({
        name: 'UserOrderHistory'
      });
    },
    
    logout() {
      this.activeDropdown = null;
      console.log('User logging out');
      
      // Clear all stored user data
      const keysToRemove = ['isLoggedIn', 'userId', 'userName', 'userMobile', 'userAddress', 'userEmail', 'status']
      keysToRemove.forEach(key => {
        localStorage.removeItem(key)
      })
      
      // Clear session storage as well
      sessionStorage.clear()
      
      // Redirect to login page
      this.$router.push({
        name: 'UserLogin'
      });
    },
    
    // Close dropdowns when clicking outside
    closeDropdowns(event) {
      if (!event.target.closest('.filter-item') && !event.target.closest('.menu-item')) {
        this.activeDropdown = null;
        this.hoveredDistrict = null;
        this.thanasForDistrict = [];
      }
    },
    
    // Location methods
    requestLocation() {
      this.isLocationLoading = true;
      this.locationStatus = { type: 'info', message: 'Requesting your location...' };
      
      // Reset to show all pharmacies when requesting location
      this.selectAllCity();
      
      // Check if geolocation is available
      if (!navigator.geolocation) {
        this.locationStatus = { type: 'error', message: 'Geolocation is not supported by your browser' };
        this.isLocationLoading = false;
        return;
      }
      
      // Request current position
      navigator.geolocation.getCurrentPosition(
        this.handleLocationSuccess,
        this.handleLocationError,
        { enableHighAccuracy: true, timeout: 10000, maximumAge: 0 }
      );
    },
    
    handleLocationSuccess(position) {
      const { latitude, longitude } = position.coords;
      console.log('Location obtained:', latitude, longitude);
      
      this.userLocation = { lat: latitude, lng: longitude };
      this.locationStatus = { type: 'success', message: 'Location obtained successfully' };
      
      // Calculate distances for each pharmacy
      this.updatePharmacyDistances();
      
      this.isLocationLoading = false;
      
      // Clear status message after a delay
      setTimeout(() => {
        this.locationStatus = null;
      }, 2000);
    },
    
    handleLocationError(error) {
      console.error('Error getting location:', error);
      
      let errorMessage = 'Failed to get your location';
      
      switch(error.code) {
        case error.PERMISSION_DENIED:
          errorMessage = 'Location permission denied. Please allow location access.';
          break;
        case error.POSITION_UNAVAILABLE:
          errorMessage = 'Location information is unavailable.';
          break;
        case error.TIMEOUT:
          errorMessage = 'Location request timed out.';
          break;
        case error.UNKNOWN_ERROR:
          errorMessage = 'An unknown error occurred.';
          break;
      }
      
      this.locationStatus = { type: 'error', message: errorMessage };
      this.isLocationLoading = false;
    },
    
    updatePharmacyDistances() {
      if (!this.userLocation) return;
      
      this.pharmacies.forEach(pharmacy => {
        // Use pharmacy coordinates from latitude and longitude fields
        if (pharmacy.coordinates && pharmacy.coordinates.lat && pharmacy.coordinates.lng) {
          pharmacy.distance = this.calculateDistance(
            this.userLocation.lat, 
            this.userLocation.lng,
            pharmacy.coordinates.lat,
            pharmacy.coordinates.lng
          );
        } else {
          // Fallback to a default distance if coordinates are missing
          console.warn(`Missing coordinates for pharmacy: ${pharmacy.name}`);
          pharmacy.distance = 999; // Set a large distance so it appears at the end
        }
      });
    },
    
    calculateDistance(lat1, lon1, lat2, lon2) {
      // Haversine formula to calculate distance between two points
      const R = 6371; // Radius of the earth in km
      const dLat = this.deg2rad(lat2 - lat1);
      const dLon = this.deg2rad(lon2 - lon1);
      const a = 
        Math.sin(dLat/2) * Math.sin(dLat/2) +
        Math.cos(this.deg2rad(lat1)) * Math.cos(this.deg2rad(lat2)) * 
        Math.sin(dLon/2) * Math.sin(dLon/2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a)); 
      const distance = R * c; // Distance in km
      return distance;
    },
    
    deg2rad(deg) {
      return deg * (Math.PI/180);
    }
  },
  created() {
    // Check authentication status first
    if (!this.checkAuthStatus()) {
      return // Exit if not authenticated
    }
    
    // Load user data
    this.loadUserData()
    
    // Load cart items
    this.loadCartItems()
    
    // Add event listener to close dropdowns when clicking outside
    document.addEventListener('click', this.closeDropdowns);
    
    // Fetch pharmacy data from API
    this.fetchPharmacies();
    
    // Check if the browser supports geolocation
    if (navigator.geolocation) {
      // Request location permission automatically on component creation
      setTimeout(() => {
        this.requestLocation();
      }, 1000);
    } else {
      this.locationStatus = { type: 'error', message: 'Geolocation is not supported by your browser' };
    }
    
    // Update document title
    document.title = 'Nearby Pharmacies';
  },
  beforeUnmount() {
    // Remove event listener when component is destroyed
    document.removeEventListener('click', this.closeDropdowns);
  },
  // Watch for cart items changes in session storage (for real-time updates)
  mounted() {
    const selectedPharmacy = sessionStorage.getItem('selectedPharmacy')
    if (selectedPharmacy) {
      const parsedPharmacy = JSON.parse(selectedPharmacy)
      this.selectedPharmacyName = parsedPharmacy.name
    }
    // Listen for storage changes to keep cart in sync
    window.addEventListener('storage', (e) => {
      if (e.key === 'cartItems') {
        this.loadCartItems()
      }
    })
  }
}
</script>

<style scoped>
/* Enhanced styles for cascading dropdown */
.cascading-menu {
  position: relative;
}

.district-item {
  position: relative;
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px 12px;
  cursor: pointer;
}

.district-item:hover {
  background-color: #f0f0f0;
}

.arrow-right {
  font-size: 14px;
  color: #666;
}

/* .thana-submenu {
  position: absolute;
  left: 100%;
  top: 0;
  min-width: 200px;
  background: white;
  border: 1px solid #ddd;
  border-radius: 4px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  z-index: 1000;
} */

.thana-item {
  padding: 8px 12px;
  cursor: pointer;
  color: #333;
}

.loading-item {
  padding: 8px 12px;
  color: #999;
  font-style: italic;
}

/* Cart specific styles */
.cart-menu {
  margin-right: 15px;
}

.cart-icon-container {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  cursor: pointer;
  border-radius: 50%;
  background-color: rgba(58, 167, 87, 0.2);
  transition: background-color 0.3s ease;
}

.cart-icon-container:hover {
  background-color: #e9ecef;
}

.cart-icon {
  width: 18px;
  height: 18px;
  color: #3aa757;
}

.cart-badge {
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: #dc3545;
  color: white;
  border-radius: 50%;
  width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  font-weight: bold;
  min-width: 18px;
}

.cart-empty {
  padding: 20px;
  text-align: center;
  color: #ccc;
}

.cart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 15px;
  border-bottom: 1px solid #444;
}

.cart-header h4 {
  margin: 0;
  font-size: 14px;
  color: #e2e2e2;
}

.cart-total {
  font-weight: bold;
  color: #3aa757;
}

.cart-items-list {
  max-height: 300px;
  overflow-y: auto;
}

.cart-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 15px;
  border-bottom: 1px solid #333;
}

.cart-item:last-child {
  border-bottom: none;
}

.cart-item-info {
  flex: 1;
}

.medicine-name {
  display: block;
  font-weight: 500;
  color: #e2e2e2;
  margin-bottom: 4px;
  font-size: 14px;
}

.cart-item-details {
  display: flex;
  justify-content: space-between;
  font-size: 11px;
  color: #ccc;
}

.remove-item {
  background: none;
  border: none;
  color: #fd1900;
  cursor: pointer;
  padding: 5px;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 11px;
  transition: background-color 0.3s ease;
}

.remove-item:hover {
  background-color: #e2e2e2;
}

.cart-actions {
  display: flex;
  gap: 10px;
  padding: 15px;
  border-top: 1px solid #444;
}

.clear-cart-btn, .checkout-btn {
  flex: 1;
  padding: 8px 12px;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-weight: 500;
  transition: all 0.3s ease;
  font-size: 11px;
}

.clear-cart-btn {
  background-color: #e74c3c;
  color: white;
}

.clear-cart-btn:hover {
  background-color: #c0392b;
}

.checkout-btn {
  background-color: #3aa757;
  color: white;
}

.checkout-btn:hover {
  background-color: #218838;
}

/* Toast message styles */
.toast-message {
  position: fixed;
  top: 80px;
  right: 20px;
  padding: 12px 20px;
  border-radius: 4px;
  z-index: 1000;
  max-width: 350px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  animation: slideIn 0.3s ease-out;
}

.toast-message.info {
  background-color: #333;
  color: #e2e2e2;
  border: 1px solid #555;
}

.toast-message.success {
  background-color: #d4edda;
  color: #155724;
  border: 1px solid #c3e6cb;
}

.toast-message.warning {
  background-color: #e2e2e2;
  color: #856404;
  border: 1px solid #ffeaa7;
}

.toast-message.error {
  background-color: #f8d7da;
  color: #721c24;
  border: 1px solid #f5c6cb;
}

@keyframes slideIn {
  from {
    transform: translateX(100%);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
}

/* Update menu bar to align items properly */
.menu-bar {
  display: flex;
  align-items: center;
  gap: 10px;
  position: absolute;
  top: 8px;
  right: 8px;
  z-index: 1000;
}

.menu-item {
  position: relative;
  cursor: pointer;
}

.pharmacy-app {
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Helvetica, Arial, sans-serif;
  background-color: #000;
  color: #e2e2e2;
  max-width: 480px;
  margin: 0 auto;
  padding: 0 8px;
}
  
.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  position: sticky;
  top: 0;
  background-color: #000;
  border-radius: 8px;
  z-index: 10;
  border-bottom: 1px solid #333;
}
  
.header h1 {
  margin: 0 0 8px 0;
  font-size: 18px;
  color: #3aa757;
  flex: 1;
}

.location-status {
  font-size: 11px;
  padding: 4px 8px;
  border-radius: 4px;
  margin-bottom: 8px;
}

.location-status.info {
  background-color: #3aa757;
}

.location-status.success {
  background-color: #3aa757;
  margin-right: 28px;
}

.location-status.error {
  background-color: #e74c3c;
}

.control-panel {
  display: flex;
  margin: 0 5px 0 5px;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 8px;
}

.filter-city {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
  
.search-bar {
  width: 100%;
}
  
.search-bar input {
  width: 100%;
  padding: 8px;
  border-radius: 4px;
  border: none;
  background-color: #333;
  color: #e2e2e2;
  box-sizing: border-box;
}

.search-bar input::placeholder {
  color: #aaa;
}

.location-button {
  padding: 4px 8px;
  font-size: 11px;
  cursor: pointer;
  border: none;
  background-color: #333;
  color: white;
  border-radius: 4px;
  white-space: nowrap;
}

.location-button:disabled {
  background-color: #444;
  cursor: not-allowed;
}

.loading-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 20px 0;
}

.loading-spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #333;
  border-top: 3px solid #3aa757;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;
}

@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}

.no-results {
  padding: 20px;
  text-align: center;
  color: #ccc;
}
  
.filter-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 11px;
  cursor: pointer;
  padding: 4px 8px;
  border: none;
  border-radius: 4px;
  position: relative;
  background-color: #333;
}
  
.down-arrow {
  border: solid #999;
  border-width: 0 2px 2px 0;
  display: inline-block;
  padding: 2px;
  transform: rotate(45deg);
  transition: transform 0.2s;
}

.down-arrow.rotated {
  transform: rotate(-135deg);
}

.pharmacy-list {
  padding: 8px;
} 
  
.pharmacy-item {
  display: flex;
  background-color: #1a1a1a;
  border: 1px solid #333;
  border-radius: 6px;
  padding: 6px;
  box-shadow: none;
  margin-bottom: 8px;
}

/* Highlight medicine cards that are in cart */
.pharmacy-item.in-cart {
  border-color: #33f543;
  box-shadow: 0 0 10px rgba(58, 167, 87, 0.3);
}
  
.pharmacy-img {
  width: 50px;
  height: 50px;
  overflow: hidden;
  border-radius: 4px;
  margin-right: 8px;
  background-color: #333;
  flex-shrink: 0;
}
  
.placeholder-img {
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  font-size: 20px;
  object-fit: cover;
}
  
.pharmacy-info {
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-width: 0;
}
  
.pharmacy-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 2px;
}
  
.pharmacy-header h3 {
  font-size: 14px;
  margin: 0;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
  
.pharmacy-badges {
  flex-shrink: 0;
}
  
.badge {
  font-size: 11px;
  padding: 1px 4px;
  border-radius: 2px;
  background-color: #3aa757;
  color: #e2e2e2;
}

.badge.distance {
  background-color: #005500;
}
  
.address {
  font-size: 11px;
  color: #ccc;
  margin-bottom: 4px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.order-button {
  background-color: #3aa757;
  color: white;
  border: none;
  border-radius: 4px;
  padding: 4px 8px;
  font-size: 11px;
  font-weight: bold;
  cursor: pointer;
  transition: background-color 0.3s ease;
  align-self: flex-start;
}

.order-button:hover {
  background-color: #2a9a47;
}

.footer {
  text-align: center;
  padding: 12px 0;
  margin-top: 16px;
  font-size: 11px;
  color: #999;
  border-top: 1px solid #333;
}

/* Updated Avatar Styles */
.user-avatar {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  overflow: hidden;
  transition: border-color 0.3s ease;
}

.user-avatar:hover {
  border-color: #e9ecef;
}

.avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

/* Menu Icons */
.icon-orders::before {
  content: "📋";
  font-size: 14px;
}

.icon-logout::before {
  content: "🚪";
  font-size: 14px;
}

/* Active filter glow effect */
.location-button.active-filter {
  background-color: #3aa757;
  color: white;
  box-shadow: 0 0 10px rgba(58, 167, 87, 0.5);
  border: 1px solid #33f543;
}

.filter-item.active-filter {
  background-color: #3aa757;
  color: white;
  box-shadow: 0 0 10px rgba(58, 167, 87, 0.5);
  border: 1px solid #33f543;
}

/* Enhanced dropdown aesthetics */
.dropdown-search-menu {
  position: absolute;
  top: 100%;
  left: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #444;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  z-index: 10;
  min-width: 150px;
  margin-top: 4px;
  backdrop-filter: blur(10px);
}

.dropdown-menu {
  position: absolute;
  top: 100%;
  right: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #444;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  z-index: 10;
  min-width: 150px;
  margin-top: 4px;
  backdrop-filter: blur(10px);
}

.dropdown-item {
  padding: 8px 12px;
  right: 0;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 11px;
  color: #e2e2e2;
  transition: all 0.2s ease;
  border-bottom: 1px solid #333;
}

.dropdown-item:last-child {
  border-bottom: none;
}

.dropdown-item:hover {
  background: linear-gradient(145deg, #3aa757, #2a9a47);
  color: white;
  transform: translateX(2px);
}

/* Enhance cart dropdown */
.cart-dropdown {
  width: 350px;
  right: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #03a003;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(10px);
}

/* Enhance menu dropdown */
.menu-dropdown {
  position: absolute;
  top: 100%;
  right: 0;
  background: linear-gradient(145deg, #2a2a2a, #1a1a1a);
  border: 1px solid #444;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  min-width: 120px;
  max-width: 140px;
  z-index: 1001;
  overflow: hidden;
  margin-top: 4px;
  backdrop-filter: blur(10px);
}

.menu-dropdown .dropdown-item:hover {
  background: linear-gradient(145deg, #2d994a, #2a9a47);
  color: white;
  transform: translateX(2px);
}

/* Enhanced thana submenu */
.thana-submenu {
  position: absolute;
  right: 100%;
  color: #e2e2e2;
  top: 0;
  min-width: 200px;
  background: linear-gradient(145deg, #0b640f, #0a2901);
  border: 1px solid #444;
  border-radius: 6px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.6);
  z-index: 1000;
  backdrop-filter: blur(10px);
}

.thana-item:hover {
  background: linear-gradient(145deg, #3aa757, #2a9a47);
  color: white;
  transform: translateX(2px);
}

/* Responsive adjustments for smaller screens */
@media (max-width: 480px) {
  .cart-dropdown {
    width: 300px;
    right: -10px;
  }
  
  .toast-message {
    right: 10px;
    max-width: 300px;
  }
  
  .header {
    padding: 12px;
  }
  
  .search-bar {
    padding: 0 12px;
  }
  
  .pharmacy-list {
    padding: 6px;
  }
}
</style>